import React from 'react';
import { Admin, Resource, CustomRoutes } from 'react-admin';
import { Route } from 'react-router-dom';
import { StyledEngineProvider } from '@mui/material/styles';
import {
  Business as AccountIcon,
  BarChart as StatsIcon,
} from '@mui/icons-material';

import { authProvider, dataProvider, i18nProvider } from './providers';
import { CustomLayout } from './layouts';
import lightTheme from './lightTheme';

import { AccountList, AccountEdit, AccountCreate } from './features/accounts';
import { ServerEdit, ServerCreate } from './features/servers';
import { LicenseEdit, LicenseCreate } from './features/licenses';
import { DocumentTypeEdit, DocumentTypeCreate } from './features/document-types';
import { NationalityEdit, NationalityCreate } from './features/nationalities';
import { KycFieldEdit, KycFieldCreate } from './features/kyc-fields';
import { UserEdit, UserCreate } from './features/users';
import { FieldMappingEdit, FieldMappingCreate } from './features/fields-mapping';
import { DefaultMappingEdit, DefaultMappingCreate } from './features/default-mapping';
import { CostCenterEdit, CostCenterCreate } from './features/cost-centers';
import { StatisticsList } from './features/statistics';
import { CreditsPage } from './features/credits';
import { PricingPage } from './features/pricing';
import { PhotoUploadPage } from './features/photo-upload';
import { KycConfigPage, BillingPage, SystemPage } from './features/admin-options';
import { Dashboard } from './features/dashboard';
import { OcrTestingPage } from './features/ocr-testing';
import { DevelopersPage } from './features/developers';

const App: React.FC = () => {
  return (
    <StyledEngineProvider injectFirst>
      <Admin
        authProvider={authProvider}
        dataProvider={dataProvider}
        i18nProvider={i18nProvider}
        theme={lightTheme}
        layout={CustomLayout}
        loginPage={false}
        dashboard={Dashboard}
        requireAuth
        disableTelemetry
      >
        {/* Main resources visible to all roles */}
        <Resource name="accounts" list={AccountList} edit={AccountEdit} create={AccountCreate} icon={AccountIcon} />
        <Resource name="statistics" list={StatisticsList} icon={StatsIcon} />

        {/* Admin resources — lists rendered inside AdminOptionsPage, edit/create need Resource routes */}
        <Resource name="servers" edit={ServerEdit} create={ServerCreate} />
        <Resource name="licenses" edit={LicenseEdit} create={LicenseCreate} />
        <Resource name="document-types" edit={DocumentTypeEdit} create={DocumentTypeCreate} />
        <Resource name="nationalities" edit={NationalityEdit} create={NationalityCreate} />
        <Resource name="kyc-fields" edit={KycFieldEdit} create={KycFieldCreate} />
        <Resource name="fields-mapping" edit={FieldMappingEdit} create={FieldMappingCreate} />
        <Resource name="default-mapping" edit={DefaultMappingEdit} create={DefaultMappingCreate} />
        <Resource name="users" edit={UserEdit} create={UserCreate} />
        <Resource name="cost-centers" edit={CostCenterEdit} create={CostCenterCreate} />

        <CustomRoutes>
          <Route path="/kyc-config" element={<KycConfigPage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/system" element={<SystemPage />} />
          <Route path="/credits" element={<CreditsPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/photo-upload" element={<PhotoUploadPage />} />
          <Route path="/ocr-testing/*" element={<OcrTestingPage />} />
          <Route
            path="/developers"
            element={
              <React.Suspense fallback={null}>
                <DevelopersPage />
              </React.Suspense>
            }
          />
        </CustomRoutes>
      </Admin>
    </StyledEngineProvider>
  );
};

export default App;
